import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GetDriverLocations,
  GetLocationRoutes,
  GetTrafficAwareDurations,
} from '../libs/GraphQL';
import {
  ARRIVAL_SCAN_EVENT,
  LOCATION_STOCKED_EVENT,
  fetchDriverActionEvents,
} from '../libs/driverActions';
import composeDriverRecords from '../utils/composeDriverRecords';
import { parseUtcDateTime } from '../utils/datetime';
import filterMonitoringRoutes from '../utils/filterMonitoringRoutes';
import loadOpsLocations from '../utils/opsLocationsCache';
import { getStockingWindowUtc } from '../utils/stockingWindow';
import usePolling from './usePolling';

// Scans refresh at most this often, independent of the (faster) position poll.
const SCAN_REFRESH_MS = 60000;

// Single source for the Monitoring tab's position-poll cadence — the hook's
// default and the value MonitoringTab passes down to the map panes / PollCountdown.
export const DEFAULT_POLL_INTERVAL_MS = 30000;

// Floor on how often a tick may re-render from partially fetched pings (see
// the page callback in fetchOnce). Leading edge only: the first page of a
// quiet stretch renders at once, further pages inside the window are folded
// into the cache and picked up by the next render — the tick always ends
// with a full compose, so nothing fetched is left unrendered.
const PROGRESSIVE_RENDER_THROTTLE_MS = 250;

// Build { [locationId]: [{ eventType, scannedAtMs, driver, driverId }] } from
// driver-action events (scans + stocking): PackingSlipQuery (arrival scan) and
// LocationStocked (completed stocking) — both share the
// locationId/_lastModified/_lastModifiedBy/data shape, so they fold into the
// same map. Each entry carries its event type: an arrival and a stocking are
// different claims, and composeDriverRecords must tell them apart. A location
// can appear on multiple routes, so we keep every event (tagged with its
// driver email) and let the composer pick the one matching each route's
// driver — same disambiguation intent as ViewStockingDashboard.
//
// The shared pipeline covers the fetch shape (previous day included, filtered to
// the 6 PM Chicago stocking window); only the projection lives here.
// `_lastModified` is a Unix timestamp in SECONDS (x1000 for JS ms).
const DRIVER_LOCATION_EVENT_TYPES = [
  ARRIVAL_SCAN_EVENT,
  LOCATION_STOCKED_EVENT,
];

async function fetchDriverActionEventsByLocationId(stockingDate) {
  // Strict mode: the caller falls back to the cached scan map on a throw, which
  // beats caching a partial map for a full SCAN_REFRESH_MS.
  const { eventsByType } = await fetchDriverActionEvents(
    DRIVER_LOCATION_EVENT_TYPES,
    stockingDate,
    { tolerateFailure: false },
  );
  const map = {};
  DRIVER_LOCATION_EVENT_TYPES.forEach((eventType) => {
    (eventsByType[eventType] || []).forEach((action) => {
      if (!action || !action.locationId) return;
      let driver = null;
      try {
        driver = JSON.parse(action.data)?.driver || null;
      } catch {
        driver = null;
      }
      const entry = {
        eventType,
        scannedAtMs: action._lastModified * 1000,
        driver: driver ? driver.toLowerCase() : null,
        driverId: action._lastModifiedBy || null,
      };
      if (!map[action.locationId]) map[action.locationId] = [];
      map[action.locationId].push(entry);
    });
  });
  return map;
}

// One getTrafficAwareDuration call per visible route — the backing table has
// no date-only access path. Legs are immutable but new ones appear as the
// route progresses, so every poll tick refetches. A failed route is
// non-fatal: it just hides its traffic fields for the tick.
async function fetchTrafficLegsByRoute(routeIds, stockingDate) {
  const results = await Promise.all(
    routeIds.map((route) =>
      GetTrafficAwareDurations(route, stockingDate).catch((err) => {
        console.error(
          `[Monitoring] getTrafficAwareDuration failed for ${route}:`,
          err,
        );
        return null;
      }),
    ),
  );
  const byRoute = {};
  results.forEach((legs, i) => {
    if (Array.isArray(legs) && legs.length > 0) byRoute[routeIds[i]] = legs;
  });
  return byRoute;
}

/**
 * Identify the drivers to poll positions for on the current tick.
 *
 * Driver set = drivers with an in-window driver-action event (scan or
 * stocking) at a location belonging to the filtered routes (row.id is the
 * locationId), UNION every driver already present in this stocking date's
 * ping-trail cache. Route rows' driverEmail is not used — the event is the
 * authoritative signal that a driver is actually working the route.
 *
 * The union keeps a driver polled for the rest of the stocking day once
 * seen, even if a later tick's events no longer surface them (a filter
 * change, or their only action today falls outside this tick's window) —
 * otherwise their trail and marker would silently stop updating mid-shift.
 * A remembered driver with no in-window event this tick falls back to the
 * email carried on their most recent cached ping.
 *
 * The GetDriverLocations query window is NOT derived here: its start depends
 * on what the ping-trail cache already holds, which only fetchOnce knows.
 * `latestScanMs` is carried through so callers can reason about how recently
 * the driver was seen.
 *
 * @param {object} args
 * @param {Object|null} args.scansByLocationId
 *        { [locationId]: [{ eventType, scannedAtMs, driver, driverId }] }
 * @param {Array}  args.locationRoutes filtered LocationRoutes rows (row.id = locationId)
 * @param {Object} [args.cachedByDriverId]
 *        this stocking date's trailCacheRef.byDriverId: { [driverId]: { pings, lastTimestampMs } }
 * @returns {Array<{
 *   driverId: string,
 *   driverEmail: string|null,
 *   latestScanMs: number|null,
 * }>}
 */
function deriveActiveDrivers({
  scansByLocationId,
  locationRoutes,
  cachedByDriverId,
}) {
  // Latest event (and its email, for ping backfill) per driver, deduped
  // across locations.
  const latestByDriverId = {};
  if (scansByLocationId) {
    const locationIds = new Set(
      (locationRoutes || []).map((row) => row && row.id).filter(Boolean),
    );
    locationIds.forEach((locationId) => {
      (scansByLocationId[locationId] || []).forEach((entry) => {
        if (!entry || !entry.driverId || entry.scannedAtMs == null) return;
        const prev = latestByDriverId[entry.driverId];
        if (!prev || entry.scannedAtMs > prev.scannedAtMs) {
          latestByDriverId[entry.driverId] = {
            scannedAtMs: entry.scannedAtMs,
            driverEmail: entry.driver || (prev ? prev.driverEmail : null),
          };
        } else if (!prev.driverEmail && entry.driver) {
          prev.driverEmail = entry.driver;
        }
      });
    });
  }
  Object.entries(cachedByDriverId || {}).forEach(([driverId, entry]) => {
    if (latestByDriverId[driverId]) {
      return;
    }
    const pings = (entry && entry.pings) || [];
    const lastEmailPing = [...pings]
      .reverse()
      .find((ping) => ping && ping.driverEmail);
    latestByDriverId[driverId] = {
      scannedAtMs: entry ? entry.lastTimestampMs : null,
      driverEmail: lastEmailPing ? lastEmailPing.driverEmail : null,
    };
  });
  return Object.entries(latestByDriverId).map(
    ([driverId, { scannedAtMs, driverEmail }]) => ({
      driverId,
      driverEmail,
      latestScanMs: scannedAtMs,
    }),
  );
}

// A ping's recorded instant in ms, or null when the timestamp is missing or
// unparseable — such a ping can't be ordered or windowed against.
function pingTimestampMs(ping) {
  const recordedAt = parseUtcDateTime(ping && ping.timestamp);
  return recordedAt ? recordedAt.getTime() : null;
}

// Identity used to dedupe pings across overlapping fetches: the backend's
// `uuid` when present, else the position + instant triple (one device can't
// record two different pings at the same instant and place).
function pingIdentity(ping) {
  return ping.uuid || `${ping.timestamp}|${ping.latitude}|${ping.longitude}`;
}

// Merge a driver's newly fetched pings into their cached trail: dedupe,
// re-sort oldest-first, and remember the newest instant so the next tick can
// ask only for what came after it. Pings with no usable timestamp are kept
// (the composer drops them) but never set `lastTimestampMs` — a bad timestamp
// must not move the window.
function mergeTrailEntry(entry, newPings) {
  const byIdentity = new Map(
    (entry ? entry.pings : []).map((ping) => [pingIdentity(ping), ping]),
  );
  newPings.forEach((ping) => {
    const identity = pingIdentity(ping);
    if (!byIdentity.has(identity)) {
      byIdentity.set(identity, ping);
    }
  });
  const pings = Array.from(byIdentity.values()).sort(
    (pingA, pingB) =>
      (pingTimestampMs(pingA) ?? -Infinity) -
      (pingTimestampMs(pingB) ?? -Infinity),
  );
  const lastTimestampMs = pings.reduce((newest, ping) => {
    const timestampMs = pingTimestampMs(ping);
    if (timestampMs === null) {
      return newest;
    }
    return newest === null || timestampMs > newest ? timestampMs : newest;
  }, null);
  return { pings, lastTimestampMs };
}

// driverEmail is nullable on the DriverLocation type; the driver-action email
// drives driverName display + event disambiguation in composeDriverRecords,
// so backfill it on any ping that came back without one.
function withDriverEmailFallback(pings, driverEmail) {
  return pings.map((ping) =>
    ping.driverEmail ? ping : { ...ping, driverEmail },
  );
}

// Reduce a windowed ping history to ONE latest ping per route —
// composeDriverRecords' ping join (pingByRouteId[p.route] = p) is
// last-write-wins and assumes exactly one latest ping per route. Backend page
// order isn't guaranteed, so compare timestamps instead of trusting array
// order; invalid/missing timestamps fall back to -Infinity so they can never
// beat a valid ping, but a route whose only pings are invalid still keeps its
// marker.
function latestPingPerRoute(pings) {
  const latestByRoute = {};
  (pings || []).forEach((ping) => {
    if (!ping || !ping.route) return;
    const timestampMs = pingTimestampMs(ping) ?? -Infinity;
    const prev = latestByRoute[ping.route];
    if (!prev || timestampMs >= prev.timestampMs) {
      latestByRoute[ping.route] = { ping, timestampMs };
    }
  });
  return Object.values(latestByRoute).map((entry) => entry.ping);
}

const byPingTime = (pingA, pingB) =>
  (pingTimestampMs(pingA) ?? -Infinity) - (pingTimestampMs(pingB) ?? -Infinity);

// One list of pings per driver, so route attribution below is judged within
// a driver's own readings — another driver's route must never claim them.
function pingsByDriver(pings) {
  const byDriver = new Map();
  (pings || []).forEach((ping) => {
    if (!ping) {
      return;
    }
    const driverKey = ping.driverId || 'unattributed';
    if (!byDriver.has(driverKey)) {
      byDriver.set(driverKey, []);
    }
    byDriver.get(driverKey).push(ping);
  });
  return Array.from(byDriver.values());
}

// Group a full ping history by route for the map's driven-path trail. Unlike
// latestPingPerRoute this keeps EVERY ping — the trail is the line, not a
// single marker.
//
// A small share of readings arrive with no route stamp (taken between
// assignments, or before the app settles on one). They are still real
// movement, so each rides with its DRIVER's route by time adjacency: the
// route that driver stamped just before it, or — for a route-less run at the
// start of the day — the first route they stamp after. Only a driver who
// never stamps any route all day drops out; there is nothing to attach to.
function trailsByRoute(pings) {
  const byRoute = {};
  const attach = (route, ping) => {
    if (!byRoute[route]) {
      byRoute[route] = [];
    }
    byRoute[route].push(ping);
  };

  pingsByDriver(pings).forEach((driverPings) => {
    let currentRoute = null;
    let leadingUnstamped = [];
    [...driverPings].sort(byPingTime).forEach((ping) => {
      if (ping.route) {
        leadingUnstamped.forEach((leading) => attach(ping.route, leading));
        leadingUnstamped = [];
        currentRoute = ping.route;
        attach(currentRoute, ping);
      } else if (currentRoute) {
        attach(currentRoute, ping);
      } else {
        leadingUnstamped.push(ping);
      }
    });
  });

  // Drivers interleave within a route bucket (the layer builders re-chain
  // per driver); each driver's own readings must stay oldest-first, which a
  // bucket-wide time sort preserves.
  Object.values(byRoute).forEach((bucket) => bucket.sort(byPingTime));
  return byRoute;
}

// Every reading a driver took that day, keyed by lower-cased email and sorted
// oldest-first — the person's own movement, with no route attribution. The
// route-keyed trails above answer "what happened on this route"; this answers
// "where was this person all day", which the driver day view draws. Readings
// with no identifiable driver are dropped: there is nobody to attribute them to.
function trailsByDriverEmail(pings) {
  const byEmail = {};
  (pings || []).forEach((ping) => {
    const email = (ping?.driverEmail || '').toLowerCase();
    if (!email) {
      return;
    }
    if (!byEmail[email]) {
      byEmail[email] = [];
    }
    byEmail[email].push(ping);
  });
  Object.values(byEmail).forEach((bucket) => bucket.sort(byPingTime));
  return byEmail;
}

/**
 * @typedef {'on-track'|'behind'|'off-track'|'not-started'|'completed'} DriverStatus
 *
 * @typedef {Object} TimelineRow
 * @property {'done'|'current'|'pending'} state
 * @property {string} positionLabel   - e.g. "Stop 4/12"
 * @property {string} stopName
 * @property {string} stopCode
 * @property {string} timeLabel       - e.g. "Stocked 10:18 AM" | "Planned 12:15 PM"
 * @property {string} durationLabel
 * @property {string} deltaLabel
 * @property {'early'|'on-time'|'over'|'late'|'eta'} deltaTier
 *
 * @typedef {Object} RouteStop
 * @property {number} stopIndex
 * @property {string} stopId
 * @property {string} stopName
 * @property {number} [latitude]
 * @property {number} [longitude]
 * @property {string} [type]
 * @property {'done'|'current'|'pending'} state
 * @property {string} plannedWindow
 * @property {number} plannedMin
 * @property {string} [stockedAt]
 * @property {string} [etaLabel]
 * @property {number|null} delta
 * @property {number|null} visitOrder       - 1-based rank by stocking time; null unless done
 * @property {number|null} driveDurationMin   - traffic-aware drive time of the leg ending here
 * @property {number|null} staticDurationMin  - same leg without traffic
 * @property {number|null} trafficDelayMin
 * @property {number|null} distanceMiles
 * @property {number|null} legDepartedAtMs
 * @property {number|null} legEtaMs           - legDepartedAtMs + drive time (measured ETA)
 * @property {string} [legEtaLabel]
 * @property {string|null} [legOrigin]     - location id the leg departed from
 * @property {string|null} [legPolyline]   - encoded road geometry of the leg
 * @property {string|null} [legDriver]     - driver email on the leg into this stop
 * @property {string|null} stockedBy       - driver email attributed to this stop
 *                                           (its legs, else its arrival scan);
 *                                           routes are sometimes split between drivers
 * @property {string} [stockedByName]
 *
 * @typedef {Object} DriverRecord
 * Composed view used by the Monitoring tab UI.
 *
 * @property {string}  driverId
 * @property {string}  [driverEmail]
 * @property {string}  driverName
 * @property {string[]} driverNames
 * @property {{email: string, name: string}[]} driverRoster
 * @property {string|null} nextStopId
 * @property {string}  routeId
 * @property {string}  routeName
 * @property {DriverStatus} status
 * @property {number|null}  etaDeltaMin
 * @property {number}  stopsCompleted
 * @property {number}  stopsTotal
 * @property {number}  [latitude]
 * @property {number}  [longitude]
 * @property {number}  [batteryLevel]
 * @property {string|null} [driverEmail]
 * @property {string|null} [route]
 * @property {string}  [timestamp]
 * @property {number}  [lastPingSecondsAgo]
 * @property {TimelineRow[]} timeline
 * @property {number}  remainingStopsCount
 * @property {RouteStop[]} routeStops
 * @property {number|null} trafficDelayTodayMin - sum of the route's traffic-leg delays
 * @property {string}  [plannedDepartureLabel]
 * @property {string}  [backAtDepotLabel]
 * @property {PingTrailPoint[]} pingTrail - the day's recorded GPS positions,
 *           oldest first; the driven path the single-route map draws
 *
 * @typedef {Object} PingTrailPoint
 * @property {number} latitude
 * @property {number} longitude
 * @property {number} timestampMs
 * @property {string} driverId
 */

/**
 * Polls live driver locations on a fixed interval and composes the result
 * with LocationRoutes + OpsLocations for the Monitoring tab.
 *
 * @param {object} args
 * @param {string} args.stockingDate - YYYY-MM-DD; the day to monitor
 * @param {Array}  [args.market]     - market filter from the parent FilterBar
 * @param {number} [args.intervalMs=30000]
 * @returns {{
 *   data: DriverRecord[],
 *   trailsByDriver: Object<string, Array>,
 *   isLoading: boolean,
 *   error: Error|null,
 *   lastPolledAt: Date|null,
 *   refetch: () => Promise<void>,
 * }}
 */
export default function useDriverLocationsPoll({
  stockingDate,
  region = null,
  market,
  fcByLocationId = null,
  // When false, nothing is fetched (no routes, ops locations, or pings) and
  // data stays empty — the Monitoring tab stays idle until the supervisor
  // applies filters, so we don't pull the whole fleet on mount.
  enabled = true,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  // Bumped by Apply Filters, Refresh and Clear All alike: re-runs the route
  // and ops-locations loads (and, through them, an immediate poll tick).
  refetchKey = 0,
  // Bumped by Apply Filters ALONE. A Refresh is an on-demand background tick
  // — same view, incremental position fetch — so only an Apply, which can
  // change the routes (and drivers) in view, resets the ping-trail cache.
  applyKey = 0,
} = {}) {
  const [data, setData] = useState([]);
  // Route-agnostic per-driver trails, painted alongside `data` from the same
  // cached readings so the driver day view draws what the records were built
  // from.
  const [trailsByDriver, setTrailsByDriver] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastPolledAt, setLastPolledAt] = useState(null);

  // LocationRoutes + OpsLocations are loaded once per stockingDate/market
  // change; only the DriverLocations ping query polls every interval.
  const [routeContext, setRouteContext] = useState({
    locationRoutes: [],
    opsLocations: [],
  });

  // Latched for the whole component lifetime in one place — async work
  // checks it before touching state after a real unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Accumulated ping history per driver, so each tick only has to fetch what
  // is new: { key: stockingDate, byDriverId: { [driverId]: { pings,
  // lastTimestampMs } } }. The trail is only meaningful for the day it was
  // gathered on, so a new stocking date — or an Apply Filters press
  // (applyKey), which can change which routes (and so which drivers) are in
  // view — starts it over. A Refresh (refetchKey alone) deliberately keeps
  // it: same filters, same trails, incremental fetch. Declared above the
  // fetch effects so a reset always commits before the tick that follows it.
  const trailCacheRef = useRef({ key: null, byDriverId: {} });
  useEffect(() => {
    trailCacheRef.current = { key: stockingDate, byDriverId: {} };
  }, [stockingDate, applyKey]);

  // Traffic legs from the most recent fetch, keyed by stocking date. Kept in
  // a ref (never awaited inline) so the traffic fan-out — one query per
  // route, the slowest fetch of a tick — never gates first paint or the
  // position fetch: composes use whatever this holds, and the tick
  // recomposes once fresh legs land. Legs are immutable (new ones only
  // append), so composing from the previous tick's legs is correct, at most
  // one tick stale.
  const trafficCacheRef = useRef({ key: null, legsByRoute: {} });

  // Bumped by every tick. A page callback from a superseded tick (a date
  // change or Apply Filters landed while its fetch was in flight) compares
  // unequal and is dropped instead of rendering yesterday's pings.
  const tickIdRef = useRef(0);

  const marketKey = JSON.stringify(market || null);

  // Load route skeletons (LocationRoutes + OpsLocations) once per filter change.
  useEffect(() => {
    if (!enabled || !stockingDate) {
      setRouteContext({ locationRoutes: [], opsLocations: [] });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await GetLocationRoutes({ stockingDate });
        if (cancelled) return;
        const allRoutes = response?.routes || [];
        const filteredRoutes = filterMonitoringRoutes(allRoutes, {
          region,
          market: JSON.parse(marketKey),
          fcByLocationId,
        });
        setRouteContext((prev) => ({
          locationRoutes: filteredRoutes,
          opsLocations: prev.opsLocations,
        }));
      } catch (err) {
        console.error('[Monitoring] GetLocationRoutes failed:', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stockingDate, marketKey, region, fcByLocationId, refetchKey, enabled]);

  // Load OpsLocations independently — it's only needed to enrich the map
  // with stop pins + names. Failure or slow pagination must NOT block the
  // driver-locations poll. Re-runs when Apply Filters bumps refetchKey; the
  // first run is cache-first, every subsequent Apply forces a fresh fetch so
  // GetOpsLocationsForMonitoring is actually called.
  const opsHasLoadedRef = useRef(false);

  // Scans (PackingSlipQuery) are cached and refreshed on a slower cadence than
  // the position poll — arrival-scan times are immutable once recorded, and the
  // per-day fetch is heavy. Intermediate position ticks reuse the cached map.
  const scansCacheRef = useRef({ key: null, fetchedAtMs: 0, scans: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const opsLocations = await loadOpsLocations(opsHasLoadedRef.current);
        if (cancelled) return;
        opsHasLoadedRef.current = true;
        setRouteContext((prev) => ({
          ...prev,
          opsLocations,
        }));
      } catch (err) {
        console.error(
          '[Monitoring] GetOpsLocations failed (map pins will be missing):',
          err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refetchKey, enabled]);

  const fetchOnce = useCallback(async () => {
    if (!isMountedRef.current) return;
    if (!enabled || !stockingDate || routeContext.locationRoutes.length === 0) {
      setData([]);
      setTrailsByDriver({});
      return;
    }
    setIsLoading(true);
    setError(null);
    tickIdRef.current += 1;
    const tickId = tickIdRef.current;
    try {
      // Routes refresh every tick: LocationRoutes is re-fetched because
      // `stockedTimeUTC` mutates as drivers complete stops — reusing the
      // first-load snapshot would freeze the map's "current" stop. Positions
      // fan out per driver AFTER routes + scans resolve, because scans are
      // what identify the active drivers and their query windows.
      //
      // Scans (PackingSlipQuery) refresh on a slower cadence (SCAN_REFRESH_MS):
      // arrival-scan times are immutable once recorded and the per-day fetch is
      // heavy, so intermediate position ticks reuse the cached map. A new
      // stockingDate busts the cache via the key check.
      const nowMs = Date.now();
      const cache = scansCacheRef.current;
      const scansFresh =
        cache.key === stockingDate &&
        cache.scans !== null &&
        nowMs - cache.fetchedAtMs < SCAN_REFRESH_MS;
      const scansPromise = scansFresh
        ? Promise.resolve(cache.scans)
        : fetchDriverActionEventsByLocationId(stockingDate).catch((err) => {
            // Non-fatal: fall back to the cached map (if any for this date) so a
            // transient failure doesn't drop on-site durations mid-shift.
            console.error(
              '[Monitoring] driver-action events fetch failed:',
              err,
            );
            return cache.key === stockingDate ? cache.scans : null;
          });

      // Traffic legs fan out over the filtered routes' friendly names — the
      // same `route` values the composer groups by. Deliberately NOT awaited
      // here (see trafficCacheRef): the tick composes from the cached legs
      // and recomposes below once the fresh ones land. Never rejects —
      // fetchTrafficLegsByRoute catches per route.
      const routeIds = Array.from(
        new Set(
          routeContext.locationRoutes.map((r) => r?.route).filter(Boolean),
        ),
      );
      const trafficPromise = fetchTrafficLegsByRoute(routeIds, stockingDate);
      const [routesResp, scansByLocationId] = await Promise.all([
        GetLocationRoutes({ stockingDate }),
        scansPromise,
      ]);
      if (!isMountedRef.current) return;
      // Refresh the cache only when we actually fetched a fresh (non-null) map.
      if (!scansFresh && scansByLocationId !== null) {
        scansCacheRef.current = {
          key: stockingDate,
          fetchedAtMs: nowMs,
          scans: scansByLocationId,
        };
      }
      const freshRoutes = filterMonitoringRoutes(routesResp?.routes || [], {
        region,
        market: JSON.parse(marketKey),
        fcByLocationId,
      });
      const effectiveRoutes =
        freshRoutes.length > 0 ? freshRoutes : routeContext.locationRoutes;

      // One windowed position query per driver seen via a driver-action event
      // on the filtered routes, unioned with every driver already cached
      // today (see deriveActiveDrivers). No events (null = fetch failed with
      // no cached fallback) and no cached drivers yet → skip the fan-out;
      // stocking progress still renders and unseen drivers show as
      // not-started.
      //
      // The window is INCREMENTAL: the trail cache already holds every ping
      // fetched so far today, so a tick asks only for what came after the
      // newest one it has (`lastTimestampMs + 1`). The first tick for a driver
      // reaches back to the stocking-day start instead of their last scan, so
      // the map draws the whole driven path rather than the stretch since the
      // most recent stop. timestampEnd is now, capped at the stocking-window
      // end so a past-date view can't pull pings from later days (route names
      // repeat weekly). A degenerate window (start at/after end, e.g. clock
      // skew) is skipped; the next tick self-heals.
      //
      // Defensive re-key: the reset effect commits before this fetch on a date
      // change, but a cache tagged with another day must never be trusted.
      if (trailCacheRef.current.key !== stockingDate) {
        trailCacheRef.current = { key: stockingDate, byDriverId: {} };
      }
      const trailCache = trailCacheRef.current;
      const stockingWindow = getStockingWindowUtc(stockingDate);
      const endMs = Math.min(nowMs, stockingWindow.end.valueOf());
      const dayStartMs = stockingWindow.start.valueOf();
      // Compose from everything cached for this stocking date — this tick's
      // drivers and the ones who failed or went quiet alike, plus whatever
      // traffic legs have landed so far. Called for the skeleton paint below,
      // from the page callback while a first window is still draining, when
      // the traffic legs resolve, and once the tick settles.
      const cachedPings = () =>
        Object.values(trailCache.byDriverId).flatMap((entry) => entry.pings);
      const composeFromCache = () => {
        const allPings = cachedPings();
        const trafficCache = trafficCacheRef.current;
        return composeDriverRecords({
          locationRoutes: effectiveRoutes,
          opsLocations: routeContext.opsLocations,
          driverPings: latestPingPerRoute(allPings),
          pingTrailsByRoute: trailsByRoute(allPings),
          scansByLocationId,
          trafficLegsByRoute:
            trafficCache.key === stockingDate ? trafficCache.legsByRoute : {},
        });
      };
      // One paint = records plus the per-driver trails, from the same cache.
      const paintFromCache = () => {
        setData(composeFromCache());
        setTrailsByDriver(trailsByDriverEmail(cachedPings()));
      };
      // A compose from this tick is only worth painting while the tick is
      // still the current one and its cache is still the live cache.
      const isCurrentTick = () =>
        isMountedRef.current &&
        tickId === tickIdRef.current &&
        trailCacheRef.current === trailCache;

      // Skeleton paint: routes, stops and stocking progress are all in hand,
      // so show them NOW — before the traffic legs and the position fan-out.
      // On the first tick this is the stops-only skeleton (empty trail
      // cache); on later ticks it refreshes stop states from the routes
      // refetch while positions are still in flight.
      paintFromCache();

      // Recompose when the traffic legs land, without ever gating the tick on
      // them. A response from a superseded tick still stores (legs are
      // immutable and keyed by stocking date) but must not paint over the
      // newer tick's compose inputs.
      trafficPromise.then((trafficLegsByRoute) => {
        if (!isMountedRef.current) {
          return;
        }
        trafficCacheRef.current = {
          key: stockingDate,
          legsByRoute: trafficLegsByRoute,
        };
        if (isCurrentTick()) {
          paintFromCache();
        }
      });

      // A page callback is only worth wiring for a driver's FIRST window of
      // the day: it spans the whole stocking day and can drain many pages,
      // so the map would otherwise stay empty until the slowest driver's last
      // page. Incremental windows are a page or two and stay all-at-once.
      //
      // The throttle is shared across this tick's drivers (one timestamp in
      // the closure), so a fleet-wide first load renders at most every
      // PROGRESSIVE_RENDER_THROTTLE_MS rather than once per driver per page.
      let lastProgressiveRenderMs = 0;
      const makePageHandler = (driverId, driverEmail) => (pageLocations) => {
        if (!isCurrentTick() || pageLocations.length === 0) {
          return;
        }
        trailCache.byDriverId[driverId] = mergeTrailEntry(
          trailCache.byDriverId[driverId],
          withDriverEmailFallback(pageLocations, driverEmail),
        );
        const renderedAtMs = Date.now();
        if (
          renderedAtMs - lastProgressiveRenderMs <
          PROGRESSIVE_RENDER_THROTTLE_MS
        ) {
          return;
        }
        lastProgressiveRenderMs = renderedAtMs;
        paintFromCache();
      };

      const driverWindows = deriveActiveDrivers({
        scansByLocationId,
        locationRoutes: effectiveRoutes,
        cachedByDriverId: trailCache.byDriverId,
      })
        .map((activeDriver) => {
          const cached = trailCache.byDriverId[activeDriver.driverId];
          const startMs =
            cached && cached.lastTimestampMs !== null
              ? cached.lastTimestampMs + 1
              : dayStartMs;
          return { ...activeDriver, startMs };
        })
        .filter((driverWindow) => driverWindow.startMs < endMs)
        .map((driverWindow) => ({
          driverId: driverWindow.driverId,
          driverEmail: driverWindow.driverEmail,
          timestampStart: new Date(driverWindow.startMs).toISOString(),
          timestampEnd: new Date(endMs).toISOString(),
          onPage:
            driverWindow.startMs === dayStartMs
              ? makePageHandler(driverWindow.driverId, driverWindow.driverEmail)
              : null,
        }));

      if (driverWindows.length > 0) {
        // allSettled: one driver's resolver failure must not blank the whole
        // fleet. Only a total failure escalates to the poll's error path. A
        // driver who failed this tick simply keeps their cached trail.
        const results = await Promise.allSettled(
          driverWindows.map((driverWindow) =>
            GetDriverLocations(
              driverWindow.driverId,
              driverWindow.timestampStart,
              driverWindow.timestampEnd,
              driverWindow.onPage,
            ),
          ),
        );
        if (!isMountedRef.current) return;
        const failures = results.filter(
          (result) => result.status === 'rejected',
        );
        failures.forEach((failure) =>
          console.error(
            '[Monitoring] GetDriverLocations failed:',
            failure.reason,
          ),
        );
        if (failures.length === results.length) {
          // Pages merged into the cache before the failure stay there but
          // any render the throttle held back is skipped with the final
          // compose below; the next successful tick shows them.
          throw failures[0].reason;
        }
        results.forEach((result, index) => {
          if (result.status !== 'fulfilled' || !Array.isArray(result.value)) {
            return;
          }
          const { driverId, driverEmail } = driverWindows[index];
          // Pages already merged by the page callback dedupe away here.
          trailCache.byDriverId[driverId] = mergeTrailEntry(
            trailCache.byDriverId[driverId],
            withDriverEmailFallback(result.value, driverEmail),
          );
        });
      }

      paintFromCache();
      setLastPolledAt(new Date());
    } catch (err) {
      console.error('[Monitoring] poll fetch failed:', err);
      if (!isMountedRef.current) return;
      setError(err);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [stockingDate, marketKey, region, fcByLocationId, enabled, routeContext]);

  const pollEnabled = enabled && routeContext.locationRoutes.length > 0;

  // Immediate fetch whenever the poll becomes possible or the route context
  // shifts (fetchOnce is recreated on routeContext change) — the supervisor
  // sees fresh positions right away instead of waiting a full interval.
  useEffect(() => {
    if (!pollEnabled) {
      setData([]);
      setTrailsByDriver({});
      return;
    }
    fetchOnce();
  }, [fetchOnce, pollEnabled]);

  // Steady-state cadence via the shared polling hook (same one Route
  // Operations uses): ticks are skipped while the tab is hidden, so a
  // backgrounded Monitoring tab stops burning fleet queries. skipFirstTick
  // because the effect above already covers the immediate fetch.
  //
  // The interval keeps its phase across context/filter changes (usePolling
  // re-arms only on enabled/intervalMs flips), so a scheduled tick can land
  // right after a context-change immediate fetch and briefly double-fetch.
  // Accepted trade-off: tying the cadence to fetchOnce identity would reset
  // the timer on every routeContext write and make the tick spacing depend
  // on when filters/context happen to change.
  usePolling(fetchOnce, {
    intervalMs,
    enabled: pollEnabled,
    skipFirstTick: true,
  });

  return {
    data,
    trailsByDriver,
    isLoading,
    error,
    lastPolledAt,
    refetch: fetchOnce,
  };
}
